// @ts-nocheck
import { css } from "@emotion/css";
import { Button, Status, TagType, Tooltip, type VirtualTableColumn } from "@snapchat/snap-design-system";
import { TrashIcon } from "@snapchat/snap-design-system-icons";

import { CopyToClipboardButton, LensIcon, Visible } from "../../../../common/components";
import { Header } from "../../../../common/components/table/Header";
import { TwoLineCell } from "../../../../common/components/table/TwoLineCell";
import { DecreaseText } from "../../../../common/components/text";
import { formatMediumDateTime } from "../../../../common/formatters";
import { lensIdIsCopiedToClipboard } from "../../../../common/model";
import { stopPropagation } from "../../../../common/utils/event";
import { type RangeModifier } from "../../../../connections/models";
import { type Permissions } from "../../../../gql-generated/aliases";
import type { GroupTags } from "../../../components/groups-table/GroupsTableColumns";
import { type LensInGroupModel } from "../../../model";

import { LensStatusCell } from "./LensStatusCell";
import { PriorityCell } from "./PriorityCell";
import { SchedulerCell } from "./SchedulerCell";

const icon = css`
    margin-left: 8px;
`;

// Unfortunately SDS doesn't export this interface
interface CellRenderer {
    row: {
        index: number;
    };
    data: Readonly<LensInGroupModel[]>;
}

function getRenderer(fn: (group: LensInGroupModel) => JSX.Element) {
    return ({ row, data }: CellRenderer) => fn(data[row.index]);
}

function getLenseTags(lens: LensInGroupModel) {
    const { removed: isLensRemoved } = lens.lens;
    const tags = [];
    if (lens.vendorData.length > 0 && !isLensRemoved) {
        tags.push({ text: "Vendor Data", status: Status.INFO });
    }
    if (lens.scheduleV3Alias != null) {
        tags.push({ text: lens.scheduleV3Alias, status: Status.SUCCESS });
    }
    if (isLensRemoved) {
        tags.push({ text: "Removed", status: Status.ERROR });
    }
    return tags;
}

interface GetColumnsParams {
    readonly: boolean;
    permissions: Permissions;
    onLensRemove: (lensId: string) => void;
    onLensPriorityChange: (lens: LensInGroupModel, priority: number, isValid: boolean) => void;
    onLensScheduleChange: (lens: LensInGroupModel, range: RangeModifier) => void;
}

export function getColumns({
    readonly,
    permissions,
    onLensRemove,
    onLensPriorityChange,
    onLensScheduleChange,
}: GetColumnsParams): VirtualTableColumn<LensInGroupModel>[] {
    return [
        {
            id: "icon",
            width: 72,
            Cell: getRenderer(({ lens }) => (
                <LensIcon
                    className={icon}
                    size={40}
                    src={lens.iconUrl}
                    removed={lens.removed}
                    source={lens.lensSourceInfo.name}
                />
            )),
        },
        {
            id: "name",
            width: 315,
            Header: <Header>Name / ID</Header>,
            Cell: getRenderer((lens) => (
                <TwoLineCell
                    text={lens.lens.name as string}
                    subText={lens.id as string}
                    tags={getLenseTags(lens) as TagType<GroupTags>[]}
                >
                    <CopyToClipboardButton
                        copyValue={lens.id as string}
                        notification={lensIdIsCopiedToClipboard}
                        tooltip="Copy Lens ID"
                    />
                </TwoLineCell>
            )),
        },
        {
            id: "scheduledDates",
            width: 270,
            Header: <Header>Scheduled Dates</Header>,
            Cell: getRenderer((lens) => (
                <Visible when={!lens.lens.removed}>
                    <SchedulerCell
                        range={lens.scheduleRange}
                        onChange={(range) => {
                            onLensScheduleChange(lens, range);
                        }}
                        readonly={readonly || !permissions.scheduleLenses || lens.lens.hasMusic}
                    />
                </Visible>
            )),
        },
        {
            id: "webSdkVersion",
            width: 102,
            Header: (
                <Header>
                    <Tooltip
                        content="Minimum Camera Kit web SDK version required to support this Lens."
                        textAlign="center"
                    >
                        <span>Min Web SDK Version</span>
                    </Tooltip>
                </Header>
            ),
            Cell: getRenderer(({ lens }) => (
                <DecreaseText>{lens.minWebSdkVersion ? lens.minWebSdkVersion : "Unknown"}</DecreaseText>
            )),
        },
        {
            id: "sdkVersion",
            width: 102,
            Header: (
                <Header>
                    <Tooltip
                        content="Minimum Camera Kit mobile SDK version required to support this Lens."
                        textAlign="center"
                    >
                        <span>Min Mobile SDK Version</span>
                    </Tooltip>
                </Header>
            ),
            Cell: getRenderer(({ lens }) => (
                <DecreaseText>{lens.minSdkVersion ? lens.minSdkVersion : "Unknown"}</DecreaseText>
            )),
        },
        {
            id: "lastUpdated",
            width: 170,
            Header: <Header>Last Updated</Header>,
            Cell: getRenderer((lens) => <DecreaseText>{formatMediumDateTime(lens.updatedAt)}</DecreaseText>),
        },
        {
            id: "priority",
            width: 102,
            Header: <Header>Priority</Header>,
            Cell: getRenderer((lens) => (
                <PriorityCell
                    priority={lens.priority}
                    onChange={(priority, isValid) => {
                        onLensPriorityChange(lens, priority, isValid);
                    }}
                    readonly={readonly || !permissions.scheduleLenses}
                />
            )),
        },

        {
            id: "lensSource",
            width: 206,
            Header: <DecreaseText>Lens Source</DecreaseText>,
            Cell: getRenderer(({ lens }) => <DecreaseText>{lens.lensSourceInfo?.name}</DecreaseText>),
        },
        {
            id: "status",
            width: 97,
            Header: <DecreaseText>Status</DecreaseText>,
            Cell: getRenderer(({ lens }) => !lens.removed && <LensStatusCell status={lens.lensStatus} />),
        },
        {
            id: "action",
            width: 63,
            Cell: getRenderer((lens) => (
                <div onClick={stopPropagation}>
                    <Button
                        shape="circle"
                        onClick={() => {
                            onLensRemove(lens.id);
                        }}
                        isDisabled={readonly || !permissions.scheduleLenses}
                        data-testid={`group-details-remove-lens-${lens.id}`}
                    >
                        <TrashIcon />
                    </Button>
                </div>
            )),
        },
    ];
}
